import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} ExtendedWarrantyRefs
 * @property {HTMLInputElement} hiddenCostInput
 * @property {HTMLInputElement} hiddenTypeInput
 * @property {HTMLInputElement} hiddenTotalInput
 * @property {HTMLInputElement} hiddenTotalCentsInput
 * @property {HTMLInputElement} hiddenProductIdInput
 */

/**
 * @extends {Component<ExtendedWarrantyRefs>}
 */
class ExtendedWarranty extends Component {
  basePriceCents = 0;
  unitWarrantyCents = 0;
  selectedWarrantyCents = 0;
  currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.basePriceCents = Number(this.dataset.basePriceCents || 0);
    this.currentQuantity = this.getQuantity();

    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input[type="radio"]')
    );

    if (!radios.length) return;

    radios.forEach((radio) => {
      radio.addEventListener('change', this.onChange);
    });

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
    section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantityUpdate);

    this.recalculatePercentageCosts();
    this.updateVariantAvailability(this.basePriceCents);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input[type="radio"]')
    );

    radios.forEach((radio) => {
      radio.removeEventListener('change', this.onChange);
    });

    const section = this.closest('.shopify-section, dialog');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
    section?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantityUpdate);
  }

  /**
   * Gets the current product quantity from the quantity selector in the same form.
   * @returns {number}
   */
  getQuantity() {
    const form = this.closest('form');
    if (!form) return 1;
    const quantityInput = form.querySelector('quantity-selector-component input[ref="quantityInput"]');
    if (!(quantityInput instanceof HTMLInputElement)) return 1;
    const qty = parseInt(quantityInput.value, 10);
    return Number.isNaN(qty) || qty < 1 ? 1 : qty;
  }

  onChange = () => {
    void this.syncFromSelection();
  };

  /** @param {Event} event */
  onQuantityUpdate = (event) => {
    const detail = /** @type {any} */ (event)?.detail;
    const quantity = Number(detail?.quantity || 0);
    if (quantity > 0) {
      this.currentQuantity = quantity;
      void this.syncFromSelection();
    }
  };

  /** @param {Event} event */
  onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event)?.detail;
    const price = Number(detail?.resource?.price || 0);
    if (!Number.isNaN(price) && price > 0) {
      this.basePriceCents = price;
      this.recalculatePercentageCosts();
      this.updateVariantAvailability(price);
    }
  };

  recalculatePercentageCosts() {
    const radios = this.querySelectorAll('input[type="radio"]');
    radios.forEach(radio => {
      // Garantia Extendida
      if (radio.hasAttribute('data-warranty-type') && radio.getAttribute('data-warranty-type') === 'percentage') {
        const rawValue = Number(radio.getAttribute('data-warranty-raw-value') || 0);
        if (rawValue > 0) {
          const newCents = Math.round((this.basePriceCents * rawValue) / 100);
          radio.setAttribute('data-warranty-cost', (newCents / 100).toFixed(2));
          this.updateOptionVisualPrice(radio, newCents);
        }
      }
      // Compra Protegida
      if (radio.hasAttribute('data-protected-purchase-type') && radio.getAttribute('data-protected-purchase-type') === 'percentage') {
        const rawValue = Number(radio.getAttribute('data-protected-purchase-raw-value') || 0);
        if (rawValue > 0) {
          const newCents = Math.round((this.basePriceCents * rawValue) / 100);
          radio.setAttribute('data-protected-purchase-cost', (newCents / 100).toFixed(2));
          this.updateOptionVisualPrice(radio, newCents);
        }
      }
    });

    void this.syncFromSelection();
  }

  updateOptionVisualPrice(radio, cents) {
    const label = radio.closest('label');
    if (!label) return;
    const priceEl = label.querySelector('.warranty-option-row__price, .protected-purchase-row__price');
    if (!priceEl) return;

    priceEl.textContent = this.formatMoney(cents);
  }

  formatMoney(cents) {
    // Si la tienda usa un formateador global de Shopify (común en muchos themes)
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      const moneyFormat = window.theme?.moneyFormat || window.theme?.strings?.moneyFormat || '${{amount}}';
      try {
        return window.Shopify.formatMoney(cents, moneyFormat);
      } catch (e) {
        // Ignorar y usar fallback manual
      }
    }

    // Fallback manual para igualar "RD$ 300.00"
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    const currency = /** @type {any} */ (window.Shopify)?.currency?.active || 'DOP';
    const symbol = currency === 'DOP' ? 'RD$' : (currency === 'USD' ? 'US$' : currency);
    
    return `${symbol} ${parts.join('.')}`;
  }

  updateVariantAvailability(currentPriceCents) {
    const currentPrice = currentPriceCents / 100;
    const radios = this.querySelectorAll('input[type="radio"]');
    
    radios.forEach(radio => {
      if (!radio.hasAttribute('data-min-price')) return;

      const minPrice = Number(radio.getAttribute('data-min-price') || 0);
      const maxPrice = Number(radio.getAttribute('data-max-price') || 999999999);

      const label = radio.closest('label');
      
      console.log('updateVariantAvailability check:', {
        currentPrice,
        minPrice,
        maxPrice,
        willEnable: (currentPrice >= minPrice && currentPrice <= maxPrice)
      });

      if (currentPrice >= minPrice && currentPrice <= maxPrice) {
        // Habilitar
        radio.disabled = false;
        if (label) label.style.display = 'flex';
      } else {
        // Deshabilitar
        radio.disabled = true;
        radio.checked = false;
        if (label) label.style.display = 'none';
      }
    });

    // Asegurarnos de que si la opción seleccionada se deshabilitó, marcamos "Sin garantía" u otra opción por defecto
    const groups = ['Garantia Extendida', 'Compra Protegida'];
    groups.forEach(groupName => {
      const radiosInGroup = this.querySelectorAll(`input[name="properties[${groupName}]"]`);
      if (radiosInGroup.length === 0) return;
      
      let validPaidOptionsCount = 0;
      radiosInGroup.forEach(r => {
        if (!r.disabled && r.value !== '') {
          validPaidOptionsCount++;
        }
      });

      // Ocultar o mostrar el contenedor del acordeón completo según si hay opciones pagadas
      const firstRadio = radiosInGroup[0];
      const accordion = firstRadio ? firstRadio.closest('details.warranty-accordion') : null;
      if (accordion) {
        accordion.style.display = validPaidOptionsCount > 0 ? '' : 'none';
      }

      const selected = Array.from(radiosInGroup).find(r => r.checked);
      if (!selected || selected.disabled || (accordion && accordion.style.display === 'none')) {
        // Buscar la primera opción válida (ej. "Sin garantía" que no tiene restricciones de precio)
        const validOption = Array.from(radiosInGroup).find(r => !r.disabled);
        if (validOption) validOption.checked = true;
      }
    });
    
    // Sincronizar de nuevo para reflejar posibles cambios
    void this.syncFromSelection();
  }

  async syncFromSelection() {
    const hiddenCostInput = /** @type {HTMLInputElement | undefined} */ (this.refs.hiddenCostInput);
    const hiddenTypeInput = /** @type {HTMLInputElement | undefined} */ (this.refs.hiddenTypeInput);

    let totalUnitCents = 0;
    let costStr = '';
    let typeStr = '';

    const selectedWarranty = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"][name="properties[Garantia Extendida]"]:checked')
    );

    if (selectedWarranty && selectedWarranty.value !== '') {
      const cost = selectedWarranty.dataset.warrantyCost || '';
      const costMajor = Number(cost || 0);
      totalUnitCents += Number.isNaN(costMajor) ? 0 : Math.round(costMajor * 100);
      costStr = cost;
      typeStr = selectedWarranty.dataset.warrantyType || '';
      this.dataset.selectedWarrantyImageUrl = selectedWarranty.dataset.warrantyImageUrl || '';
    } else {
      this.dataset.selectedWarrantyImageUrl = '';
    }

    const selectedProtected = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"][name="properties[Compra Protegida]"]:checked')
    );

    if (selectedProtected && selectedProtected.value !== '') {
      const cost = selectedProtected.dataset.protectedPurchaseCost || '';
      const costMajor = Number(cost || 0);
      totalUnitCents += Number.isNaN(costMajor) ? 0 : Math.round(costMajor * 100);
      // Asignamos cost/type para hidden inputs si no hay Garantía o si Compra Protegida los sobreescribe
      if (!costStr) costStr = cost;
      if (!typeStr) typeStr = selectedProtected.dataset.protectedPurchaseType || '';
    }

    this.unitWarrantyCents = totalUnitCents;
    this.selectedWarrantyCents = totalUnitCents * this.currentQuantity;

    if (hiddenCostInput) hiddenCostInput.value = costStr;
    if (hiddenTypeInput) hiddenTypeInput.value = typeStr;

    this.updateTotal();
  }

  updateTotal() {
    const hiddenTotalInput = /** @type {HTMLInputElement | undefined} */ (this.refs.hiddenTotalInput);
    const hiddenTotalCentsInput = /** @type {HTMLInputElement | undefined} */ (this.refs.hiddenTotalCentsInput);

    // Costo unitario de la garantía → para el carrito (cart-summary.liquid multiplica por quantity)
    const unitCents = this.unitWarrantyCents || 0;
    const unitMajor = (unitCents / 100).toFixed(2);

    // Total PDP = precio base + garantía × cantidad → para mostrar visualmente
    const pdpTotalCents = this.basePriceCents + this.selectedWarrantyCents;
    const pdpTotalMajor = (pdpTotalCents / 100).toFixed(2);

    if (hiddenTotalInput) {
      hiddenTotalInput.value = unitMajor;
    }
    if (hiddenTotalCentsInput) {
      hiddenTotalCentsInput.value = String(unitCents);
    }

    // Exponer en el DOM para que product-form.js lo lea
    this.dataset.totalCents = String(pdpTotalCents);
    this.dataset.warrantyUnitCents = String(unitCents);

    // No sumar el precio visualmente en el PDP a petición del usuario
    return;
  }
}

if (!customElements.get('extended-warranty-component')) {
  customElements.define('extended-warranty-component', ExtendedWarranty);
}